import { getPendingHostListingInviteId, getPendingInviteId, getPendingStaffInviteToken, getPendingVenueReferralData } from './deepLinkHandler';
import { getLastActiveRole, getLastActiveRolePath, getLandingPathForRememberedRole } from './lastActiveRole';

/**
 * Mirrors the ProtectedRoute allow-list byte-for-byte: which top-level
 * path prefixes can a given user actually render? Admins bypass every
 * guard, so they can enter any shell. Everyone else is restricted by
 * `user.role` — NOT `available_roles`. The backend always seeds
 * `available_roles` with 'guest' for every authenticated user, so a
 * check on `available.has('guest')` would let a host/venue_owner/staff
 * user inherit a remembered /guest/… path and get bounced into a
 * /login <-> /guest/… infinite redirect (blank white screen, Feb 2026
 * repro). Keep this in lockstep with ProtectedRoute.js.
 */
function canUserRenderPath(user, path) {
  if (!user || !path) return false;
  if (user.role === 'admin') return true;
  if (path.startsWith('/guest/')) return user.role === 'guest';
  if (path.startsWith('/host/')) return user.role === 'host';
  if (path.startsWith('/venue/')) return user.role === 'venue_owner';
  if (path.startsWith('/staff-admin/') || path.startsWith('/staff-app/')) {
    return user.role === 'staff';
  }
  // Public / non-role-gated paths (auth, marketing, invites, /notifications
  // etc.) fall through to true — the caller only feeds us role-shell paths.
  return true;
}

/**
 * Returns the post-login default route for a user based on role/status.
 * Single source of truth used by AppRoutes (login redirect), the smart
 * root router, and any other place that needs to land a user "into the app".
 *
 * Priority order:
 *   1. Pending deep-link invites (staff / host listing / guest invite / venue referral)
 *   2. Last-active role shell the user was inside (persisted across logouts)
 *   3. Default landing for `user.role`
 */
export function getDefaultRoute(user) {
  if (!user) return '/login';

  // 1. Pending deep-link invites always win — they're how the user got here.
  const pendingStaffInviteToken = getPendingStaffInviteToken();
  if (pendingStaffInviteToken) {
    return `/staff-invite/${pendingStaffInviteToken}`;
  }
  const pendingInviteId = getPendingInviteId();
  if (pendingInviteId) {
    // No role gate: accounts hold multiple roles and the invite entry page
    // auto-switches any active role back to guest on mount (iter 238 fix).
    // Gating on role === 'guest' silently dropped invites for users whose
    // last-active role was host/venue/staff/admin.
    return `/invite/${pendingInviteId}`;
  }
  const pendingHostListingInviteId = getPendingHostListingInviteId();
  if (pendingHostListingInviteId) {
    // Host→host invites identify the recipient as a host candidate. Skip
    // the redundant "Host or Venue?" picker and route to the host intro.
    // NO role gate — same multi-role reasoning as pendingInviteId above.
    return '/host-intro-1';
  }
  const pendingVenueReferral = getPendingVenueReferralData();
  if (pendingVenueReferral && !['admin', 'staff'].includes(user.role)) {
    return '/venue/intro';
  }

  // 2. Honour the last-active role shell so the user lands back where they
  // were. Falls through to the default-by-role logic when no remembered
  // role exists (first-ever sign-in) or the user no longer has access.
  const lastRole = getLastActiveRole();
  if (lastRole) {
    const remembered = getLandingPathForRememberedRole(lastRole, user, getLastActiveRolePath());
    // Belt-and-braces guard: only trust the remembered path when the user
    // can actually render its shell. Otherwise ProtectedRoute would bounce
    // them to /login, /login would call us again, and we'd loop forever
    // on a blank screen. This mirrors the ProtectedRoute allow-list.
    if (remembered && canUserRenderPath(user, remembered)) return remembered;
  }

  // 3. Fall back to the default landing for the user's stored role.
  if (user.role === 'guest') return '/guest/home';
  if (user.role === 'staff') {
    return user.staff_role === 'admin' ? '/staff-admin/people' : '/staff-app/people';
  }
  if (user.role === 'host') return '/host/active-qr';
  if (user.role === 'venue_owner') {
    if (user.venue_status === 'approved') return '/venue/analytics';
    // Setup-mode owners get full access to the venue app (bottom nav +
    // analytics/scan/messages/settings) — a banner on the home page nudges
    // them to finish the checklist instead of gating them behind a
    // separate screen. `/venue/setup-mode` is now reached via that banner.
    if (user.venue_status === 'setup_mode') return '/venue/analytics';
    // Pending admin approval OR temporarily on hold — the owner still
    // owns a real venue application and must be able to edit photos,
    // hours, vibe, socials, etc. Route them into the venue app and let
    // /venue/analytics render the appropriate under-review / on-hold
    // banner. This replaces the old dead-end "Application in progress"
    // screen that blocked owners once they'd bounced through the guest
    // app.
    if (user.venue_status === 'pending' || user.venue_status === 'submitted' || user.venue_status === 'on_hold') return '/venue/analytics';
    if (user.venue_status === 'draft' || user.venue_status === 'revisions_requested') return '/venue/intro';
    return '/venue/onboarding/complete';
  }
  return '/admin/dashboard';
}
